import { html, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { CustomRulesEvents, GridType, RuleType, Targets, xdrOptions, Type } from '../../../../customRulesGrid/types';
import { TagTypes } from '../../../../../tagsManager/types/tag-types';
import { FontSizes, LineHeights } from '../../../../../types/common/types';
import { SelectedTagsWebComponent } from './SelectedTagsWebComponent';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { TARGET_CHANGE, YARA_VALUE_CHANGED } from "../../../constants";
import {GzSelectedListItemGeneratorWebComponent} from "../../../../../ui/gzSelectedListItemGenerator";
import {EventTypes} from "../../../../../ui/gzSelectedListItemGenerator/types";

/**
 * @summary Type here the description of this component
 * @tag rule-targets
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('rule-targets')
export class RuleTargetsWebComponent extends LitElement {
  @property({ type: Boolean, attribute: true })
  public hidden: boolean;

  @property({ type: Boolean, attribute: false })
  public hasAccessOnCompany: boolean = false;

  @property({ type: Boolean, attribute: false })
  public hasEditInfoStep3: boolean = false;

  @property({ type: Object, attribute: false, reflect: false })
  public company: any;

  @property({ type: Boolean })
  isPartnerCompany = false;

  @property({ type: Object, attribute: false, reflect: false })
  selectedTagsComponent: SelectedTagsWebComponent;

  @property({ type: Object, attribute: false, reflect: false })
  rule: any = null;

  @property({ type: Object, attribute: false, reflect: false })
  availableEndpointTags: any = null;

  @property({ type: Array, attribute: false, reflect: false })
  availableCompanies: any = null;

  @property({ type: Array, attribute: false, reflect: false })
  availableCompaniesWithEdr: any = null;

  @property({ type: Boolean })
  isGZ = false;

  @property({ type: String, attribute: false, reflect: false })
  gridType;

  @property({ type: Array, attribute: false, reflect: false })
  public userCompany;

  @query('#endpoint-tags')
  private readonly endpointTags;

  @query('#companies')
  private readonly companies;

  @query('#targets')
  private readonly targets: GzRadioGroupWebComponent;

  @query('#companyRadioButton')
  public companyRadioButton: any;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class=${this.hidden ? 'hidden' : ''}>
        <div class="d-flex flex-column">
          <gz-text class="section-title" fontSize="18" fontweight="500">${translateFn('Rule targets')}</gz-text>
          ${this.renderInfoBox()}
          <div class="d-flex flex-row mt-3 mb-2 container">
            <gz-tooltip class="label" tooltipText="${translateFn('Apply rule on targets from')}" isEllipsis>
              <gz-text class="d-inline" lineHeight="${LineHeights.SMALL}" fontSize="13">${translateFn('Apply rule on targets from')}</gz-text>
            </gz-tooltip>
            ${this.renderTargets()}
          </div>
          ${this.renderCompanies()}
          ${this.renderEndpointTags()}
        </div>
      </div>
    `;
  }

  private renderInfoBox() {
    return !this.hasEditInfoStep3
      ? nothing
      : html`
          <gz-info-box .severity="${'informative'}"
            >${translateFn(
              'The rule you are editing is created by another user and it applies also to targets out of your scope. ' +
                'Before saving your changes, confirm them with the rule creator or a GravityZone user with Company Administrator role. ' +
                'Alternatively, you can create another rule with the intended settings and targets.'
            )}
          </gz-info-box>
        `;
  }

  private renderTargets() {
    const isEndpointTagsEnabled = this.checkEndpointTagsAvailability();
    if (!this.hasAccessOnCompany && isEndpointTagsEnabled) {
      if (this.rule.targets.type === Targets.COMPANY) {
        this.rule.targets.type = Targets.ENDPOINT;
      }

      if (!this.rule.targets.endpointTags) {
        this.rule.targets.endpointTags = [];
      }
    }

    if (!isEndpointTagsEnabled && (this.rule.targets.endpointTags || !this.hasAccessOnCompany)) {
      this.rule.targets = {};
    }

    return html`
          <div class="d-flex flex-column">
            <gz-radio-group
              selectedOption="${this.rule.targets.type}"
              @radioSelectionChanged="${this.handleTargets}"
              groupName="targets"
              id="targets"
              .propagateDisabledToChildren=${false}
            >
            <gz-radio id="companyRadioButton" .disabled="${!this.hasAccessOnCompany}" value="${Targets.COMPANY}"
                      label="${this.company.companyName}"></gz-radio>
            <gz-radio .disabled="${!isEndpointTagsEnabled}" value="${Targets.ENDPOINT}"
                      label="${translateFn('Endpoint tags')}"></gz-radio>
            ${this.renderGzRadio()}
            </gz-radio-group>
            ${this.renderEndpointTagsInfo()}
          </div>
        `;
  }

  private checkAccessOnOnlyOwnCompany() {
    if (!this.availableCompanies || this.availableCompanies.length === 0 || this.availableCompanies.length > 1) {
      return false;
    }

    return this.availableCompanies[0]._id === this.userCompany.companyId;
  }

  private renderGzRadio() {
    if (!this.isPartnerCompany || this.checkAccessOnOnlyOwnCompany() || this.isGZ) {
      return;
    }

    return html`
        <gz-radio value="${Targets.COMPANIES}" label="${translateFn('Companies')}" ></gz-radio>`;
  }

  private checkEndpointTagsAvailability() {
    if (this.rule.type !== RuleType.BASIC || this.gridType === GridType.DETECTION) {
      return true;
    }

    let hasXdrRules = false;

    const queryRules = this.rule.query.rules;
    const xdrAvailableOptions = xdrOptions[this.rule.query.type] ?? [];
    queryRules.forEach(rule => {
      if (xdrAvailableOptions.includes(rule.criteriaValue)) {
        hasXdrRules = true;
      }
    });

    return !hasXdrRules;
  }

  private handleDeleteTag(event) {
    const deletedTag = event.detail;
    this.rule.targets.endpointTags = this.rule.targets.endpointTags.filter(item => item.tagId !== deletedTag.value);
    this.requestUpdate('rule');
  }

  private handleDeleteCompany(event) {
    const deletedCompany = event.detail;
    this.rule.targets.companiesIds = this.rule.targets.companiesIds.filter(item => item !== deletedCompany.value);
    this.requestUpdate('rule');
  }

  private getEndpointTagsOptions() {
    const tagsOptions: any = [];

    if (!this.rule.targets.endpointTags) {
      this.rule.targets.endpointTags = [];
    }

    for (const tag of this.availableEndpointTags) {
      const option: any = {
        label: tag.name,
        value: tag._id,
        additionalData: {
          type: tag.type ? TagTypes.TAG_TYPE_AUTOMATIC : TagTypes.TAG_TYPE_CUSTOM,
        },
        selected: this.rule.targets.endpointTags.find(item => item.tagId === tag._id) !== undefined,
        isEllipsis: true,
      };
      if (this.isPartnerCompany) {
        option.parent = tag.company.name;
      }
      tagsOptions.push(option);
    }

    return tagsOptions;
  }

  private renderCompanies() {
    if (Number(this.rule.targets.type) !== Targets.COMPANIES) {
      return nothing;
    }

    const companiesOptions = this.getCompaniesOptions();

    const companiesSelection = new GzSelectedListItemGeneratorWebComponent();
    companiesSelection.canSelectAll = true;
    companiesSelection.strings = {
      sectionLeftTitle: translateFn('Companies'),
      sectionRightTitle: translateFn('Selected companies'),
      searchPlaceholderString: translateFn('Search company'),
      noSearchResultsString: translateFn('No results found'),
      allLabel: translateFn('All'),
      emptyStateText: translateFn('No companies selected yet'),
    };
    companiesSelection.filterOptions = companiesOptions;
    companiesSelection.addEventListener(EventTypes.CHANGE_SELECTED, this.handleCompaniesChange.bind(this));


    return companiesSelection;
  }

  private getCompaniesOptions() {
    const options: any = [];

    if (!this.rule.targets.companiesIds) {
      this.rule.targets.companiesIds = [];
    }

    for (const company of this.availableCompaniesWithEdr) {
      options.push({
        label: company.name,
        value: company._id,
        selected: this.rule.targets.companiesIds.find(item => item === company._id) !== undefined,
      });
    }

    return options;
  }

  private renderEndpointTags() {
    if (Number(this.rule.targets.type) !== Targets.ENDPOINT) {
      return nothing;
    }

    const tagsOptions = this.getEndpointTagsOptions();

    this.selectedTagsComponent = new SelectedTagsWebComponent();
    this.selectedTagsComponent.isPartnerCompany = this.isPartnerCompany;
    this.selectedTagsComponent.tags = tagsOptions.filter(option => option.selected);
    this.selectedTagsComponent.addEventListener(CustomRulesEvents.DELETE, this.handleDeleteTag.bind(this));

    return this.isPartnerCompany
      ? html` <div class="d-flex flex-row">
          <div class="d-flex flex-column section">
            <gz-text fontSize="${FontSizes.PARAGRAPH_11}" class="section-title text-uppercase mb-1" fontWeight="500">
              ${translateFn('Endpoint tags')}
            </gz-text>
            <gz-tags-enum-list
              id="endpoint-tags"
              .filterOptions="${tagsOptions}"
              .placeholder="${translateFn('Search endpoint tags')}"
              .noSearchResultsString="${translateFn('No results found')}"
              @optionChanged="${this.handleTagsChange}"
            ></gz-tags-enum-list>
          </div>
          ${this.selectedTagsComponent}
        </div>`
      : html` <div class="d-flex flex-row">
          <div class="d-flex flex-column section">
            <gz-text fontSize="${FontSizes.PARAGRAPH_11}" class="section-title text-uppercase mb-1" fontWeight="500">
              ${translateFn('Endpoint tags')}
            </gz-text>
            <gz-tags-enum-list
              id="endpoint-tags"
              .filterOptions="${tagsOptions}"
              .placeholder="${translateFn('Search endpoint tags')}"
              .noSearchResultsString="${translateFn('No results found')}"
              .allAutoTagsLabel="${translateFn('All automatic tags')}"
              .allCustomTagsLabel="${translateFn('All custom tags')}"
              @optionChanged="${this.handleTagsChange}"
              canSelectAll
            ></gz-tags-enum-list>
          </div>
          ${this.selectedTagsComponent}
        </div>`;
  }

  private renderEndpointTagsInfo() {
    if (this.isGZ || this.gridType === GridType.DETECTION) {
      return;
    }

    return html `
      <gz-text class="tag-info" fontSize="${FontSizes.PARAGRAPH_12}">
        ${translateFn('Rules targeting the company apply to both EDR and XDR technologies.')}
        <br/>
        ${translateFn('Rules based on endpoint tags apply only to the EDR technology.')}
      </gz-text>
    `;
  }

  private handleCompaniesChange(e: CustomEvent) {
    const companies = e.detail.selectedItems;
    this.rule.targets.companiesIds = companies.map(company => company.value);
  }

  private handleTagsChange() {
    this.selectedTagsComponent.tags = this.endpointTags.getCheckedOptions();

    const selectedTagIds = this.selectedTagsComponent.tags.map(tag => tag.value);
    const selectedTags = this.availableEndpointTags.filter(tag => selectedTagIds.includes(tag._id));

    this.rule.targets.endpointTags = selectedTags.map(tag => {
      return { tagId: tag._id, companyId: tag.company.id };
    });
  }

  private handleTargets(e: CustomEvent) {
    this.rule.targets.type = Number(e.detail.selectedValue);

    if (this.rule.targets.type === Targets.COMPANY) {
      this.rule.targets.companyId = this.company.companyId;
    }

    this.requestUpdate('rule');

    this.dispatchEvent(new CustomEvent(TARGET_CHANGE, {
      detail: {
        targets: this.rule.targets,
      }
    }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rule-targets': RuleTargetsWebComponent;
  }
}
